import { inject, Injectable } from '@angular/core';
import { DomSanitizer, SafeResourceUrl, SafeHtml } from '@angular/platform-browser';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { FileUploadService } from './files-and-attachments/file-upload.service';

export interface DocumentViewerResult {
  fileType: 'pdf' | 'docx' | 'xlsx' | 'pptx' | 'image' | 'msg' | 'other';
  isOfficeDocument: boolean;
  isPdf: boolean;
  isImage: boolean;
  /**
   * Canonical, PERSISTABLE identity of the document — a plain S3 URL, a blob: URL,
   * or a legacy link. Safe to write to the database (linkToContract / linkToBaa / linkToCoi).
   * NEVER a presigned URL: those carry a signature and expire in ~15 minutes, so storing
   * one would rot silently. Use `fetchUrl` to actually load the bytes.
   */
  directLink: string;
  /**
   * EPHEMERAL url used to fetch the document right now — presigned when the file lives in
   * our S3 attachments bucket, otherwise identical to `directLink`. Expires (~15 min).
   * Never persist this, never cache it beyond the current view.
   */
  fetchUrl: string;
  /** S3 object key when this document lives in our attachments bucket; used to re-mint `fetchUrl`. */
  s3Key: string | null;
  safeViewerUrl: SafeResourceUrl;
  safePdfHtml?: SafeHtml;
  fileName: string;
  /** True when preview cannot be shown (e.g., Office doc with blob URL before upload) */
  previewUnavailable?: boolean;
  /** Reason why preview is unavailable (for display to user) */
  previewUnavailableReason?: string;
}

@Injectable({
  providedIn: 'root'
})
export class DocumentViewerService {
  private sanitizer = inject(DomSanitizer);
  private fileUploadService = inject(FileUploadService);

  /**
   * Main method to process any file URL or attachment object.
   *
   * Async because documents stored in our S3 attachments bucket are no longer publicly
   * readable — the bucket is locked down, so we mint a short-lived presigned GET URL at
   * view time. Inputs that aren't in our bucket (blob: previews before upload, legacy
   * links from the old app) pass straight through.
   */
  async processDocument(input: string | any): Promise<DocumentViewerResult> {
    const fileUrl = this.extractFileUrl(input);
    const fileName = this.extractFileName(input);
    const fileType = this.determineFileType(fileName);

    const s3Key = this.toS3Key(fileUrl);
    const fetchUrl = await this.resolveFetchUrl(fileUrl, s3Key);

    // Check if preview is unavailable (Office doc with blob URL - not yet uploaded)
    const previewUnavailable = this.isOfficeDocument(fileType) && this.isBlobUrl(fileUrl);
    const previewUnavailableReason = previewUnavailable ? 'Preview will be available after saving' : '';

    const result: DocumentViewerResult = {
      fileType,
      isOfficeDocument: this.isOfficeDocument(fileType),
      isPdf: fileType === 'pdf',
      isImage: this.isImageType(fileType),
      directLink: fileUrl,
      fetchUrl,
      s3Key,
      safeViewerUrl: this.getSafeViewerUrl(fetchUrl, fileType),
      fileName,
      previewUnavailable,
      previewUnavailableReason
    };

    // Add PDF HTML embed if needed
    if (result.isPdf) {
      result.safePdfHtml = this.buildPdfHtml(fetchUrl);
    }

    return result;
  }

  /**
   * Re-mint the presigned `fetchUrl` on an already-processed document.
   *
   * Presigned URLs expire in ~15 minutes. A viewer left open past that point (or an Office
   * doc Microsoft fetches late) will fail to load; call this from the consumer's error
   * handler to get a fresh URL rather than leaving the user with a dead frame.
   * Returns a NEW result object; documents that aren't in our bucket are returned unchanged.
   */
  async refreshFetchUrl(result: DocumentViewerResult): Promise<DocumentViewerResult> {
    if (!result?.s3Key) {
      return result;
    }

    const fetchUrl = await this.resolveFetchUrl(result.directLink, result.s3Key);
    const refreshed: DocumentViewerResult = {
      ...result,
      fetchUrl,
      safeViewerUrl: this.getSafeViewerUrl(fetchUrl, result.fileType)
    };

    if (refreshed.isPdf) {
      refreshed.safePdfHtml = this.buildPdfHtml(fetchUrl);
    }

    return refreshed;
  }

  /**
   * Mint a presigned GET URL for an S3 key, falling back to the canonical URL.
   *
   * The fallback matters: if the presign endpoint is unreachable the viewer behaves exactly
   * as it did before this change (works against a public bucket, 403s against a locked one)
   * instead of rendering nothing at all.
   */
  private async resolveFetchUrl(canonicalUrl: string, s3Key: string | null): Promise<string> {
    if (!s3Key) {
      return canonicalUrl;
    }

    try {
      return await this.fileUploadService.getPresignedGetUrl(s3Key);
    } catch (error) {
      console.error('[DocumentViewer] Failed to mint presigned GET URL for key:', s3Key, error);
      return canonicalUrl;
    }
  }

  private buildPdfHtml(url: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(
      `<object width="700" height="1000" data="${url}" type="application/pdf">` +
      `<embed src="${url}" type="application/pdf" /></object>`
    );
  }

  /**
   * Reduce any stored document reference to a bare S3 object key, or null when the document
   * doesn't live in an S3 bucket we can presign against.
   *
   * Stored values are NOT uniform, which is the main hazard here:
   *   - `https://<bucket>.s3.us-east-1.amazonaws.com/<key>`  ← what uploads persist today
   *     (new-attachment saves the presigned PUT url minus its query string, so it's REGIONAL)
   *   - `https://<bucket>.s3.amazonaws.com/<key>`            ← environment.s3Location form
   *   - `https://s3.<region>.amazonaws.com/<bucket>/<key>`   ← path-style
   *   - `contracts/ABC123/1787083116156_x.pdf`               ← bare key (ETL'd records)
   * A naive "strip environment.s3Location" would miss the regional form — i.e. every
   * attachment uploaded through the app — so match structurally instead of by prefix.
   */
  private toS3Key(value: string): string | null {
    if (!value) return null;

    // Local previews and inline data can't be presigned; they're already loadable as-is.
    if (value.startsWith('blob:') || value.startsWith('data:')) return null;

    // Bare key or filename (ETL'd data) — already the thing we want.
    if (!value.startsWith('http')) {
      return value.replace(/^\/+/, '') || null;
    }

    let url: URL;
    try {
      url = new URL(value);
    } catch {
      return null;
    }

    const host = url.hostname;
    const path = url.pathname.replace(/^\/+/, '');
    const decode = (segment: string) => {
      try {
        return decodeURIComponent(segment);
      } catch {
        return segment;
      }
    };

    // Virtual-hosted style: <bucket>.s3.amazonaws.com | <bucket>.s3.<region>.amazonaws.com
    if (/^.+?\.s3(?:[.-][a-z0-9-]+)?\.amazonaws\.com$/.test(host)) {
      return decode(path) || null;
    }

    // Path style: s3.amazonaws.com/<bucket>/<key> | s3.<region>.amazonaws.com/<bucket>/<key>
    if (/^s3(?:[.-][a-z0-9-]+)?\.amazonaws\.com$/.test(host)) {
      const segments = path.split('/');
      segments.shift(); // drop the bucket segment
      const key = segments.join('/');
      return key ? decode(key) : null;
    }

    // Anything else (legacy app-domain links from the old system, CloudFront, etc.)
    // is not ours to presign — hand it back untouched.
    return null;
  }

  /**
   * Get safe viewer URL based on file type
   * Note: Blob URLs (blob:http://...) cannot be used with Microsoft Office Online viewer
   * because Microsoft's servers cannot access local blob URLs.
   */
  private getSafeViewerUrl(fileUrl: string, fileType: string): SafeResourceUrl {
    if (fileType === 'pdf' || this.isImageType(fileType)) {
      return this.sanitizer.bypassSecurityTrustResourceUrl(fileUrl);
    }

    if (this.isOfficeDocument(fileType)) {
      // Check if this is a blob URL - Microsoft Office viewer cannot access blob URLs
      if (this.isBlobUrl(fileUrl)) {
        return this.sanitizer.bypassSecurityTrustResourceUrl('');
      }
      const viewerBaseUrl = this.getOfficeViewerBaseUrl(fileType);
      const viewerUrl = `${viewerBaseUrl}${encodeURIComponent(fileUrl)}`;
      return this.sanitizer.bypassSecurityTrustResourceUrl(viewerUrl);
    }

    return this.sanitizer.bypassSecurityTrustResourceUrl(fileUrl);
  }

  /**
   * Check if URL is a blob URL (local browser URL that can't be accessed externally)
   */
  private isBlobUrl(url: string): boolean {
    return url.startsWith('blob:');
  }

  /**
   * Determine appropriate Office viewer URL
   * IMPORTANT: Using the generic view.officeapps.live.com for ALL Office documents
   * because excel.officeapps.live.com returns 404 for some S3 configurations.
   * The generic viewer handles Word, Excel, and PowerPoint files correctly.
   */
  private getOfficeViewerBaseUrl(fileType: string): string {
    // Use generic Office viewer for all document types (Word, Excel, PowerPoint)
    return 'https://view.officeapps.live.com/op/embed.aspx?src=';
  }

  /**
   * Extract file URL from various input formats
   */
  private extractFileUrl(input: string | any): string {
    if (typeof input === 'string') {
      return input;
    }

    // Handle attachment object
    // directLink may be a full S3 URL, a blob URL, or a bare filename (from ETL'd data)
    if (input?.directLink) {
      if (input.directLink.startsWith('http') || input.directLink.startsWith('blob:')) {
        return input.directLink;
      }
      // Bare filename — prepend S3 location
      return `${environment.s3Location}${input.directLink}`;
    }

    if (input?.attachmentData?.directLink) {
      if (input.attachmentData.directLink.startsWith('http')) {
        return input.attachmentData.directLink;
      }
      return `${environment.s3Location}${input.attachmentData.directLink}`;
    }

    if (input?.s3filename) {
      return `${environment.s3Location}${input.s3filename}`;
    }

    if (input?.attachmentData?.s3filename) {
      return `${environment.s3Location}${input.attachmentData.s3filename}`;
    }

    return '';
  }

  /**
   * Extract filename from various input formats
   */
  private extractFileName(input: string | any): string {
    if (typeof input === 'string') {
      return input.split('/').pop()?.split('?')[0] || 'document';
    }

    // Prioritize fields that contain actual filenames with extensions
    // over display names (input.name) which often lack extensions
    return input?.attachmentData?.originalFileName ||
           input?.originalFileName ||
           input?.attachmentData?.s3filename ||
           input?.s3filename ||
           input?.attachmentData?.name ||
           input?.name ||
           'document';
  }

  /**
   * Determine file type from filename
   */
  private determineFileType(fileName: string): any {
    const extension = fileName.split('.').pop()?.toLowerCase() || '';

    const typeMap: { [key: string]: string } = {
      'pdf': 'pdf',
      'docx': 'docx',
      'doc': 'docx',
      'xlsx': 'xlsx',
      'xls': 'xlsx',
      'pptx': 'pptx',
      'ppt': 'pptx',
      'jpg': 'image',
      'jpeg': 'image',
      'png': 'image',
      'gif': 'image',
      'bmp': 'image',
      'webp': 'image',
      'msg': 'msg',
      'htm': 'pdf',
      'html': 'pdf'
    };

    return typeMap[extension] || 'other';
  }

  /**
   * Check if file type is an Office document
   */
  private isOfficeDocument(fileType: string): boolean {
    return ['docx', 'xlsx', 'pptx'].includes(fileType);
  }

  /**
   * Check if file type is an image
   */
  private isImageType(fileType: string): boolean {
    return fileType === 'image';
  }

  /**
   * Download a file
   */
  downloadFile(url: string, fileName?: string): void {
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName || this.extractFileName(url);
    link.click();
  }

  /**
   * Open file in new tab
   */
  openInNewTab(url: string): void {
    window.open(url, '_blank');
  }
}